import { Box, BoxProps } from '@mui/material';
import * as R from 'ramda';
import useWidth from '../../components/Hooks/useWidth';
import ProductAvailabilityChip from '../../components/Product/ProductAvailabilityChip';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../translation/TranslationContext';
import { AvailabilityEnum, CompactAvailability } from './ProductTypes';

type ProductAvailabilityRendererProps = {
  availabilities: AvailabilityEnum[];
  useFutureStockAsPreOrder?: boolean;
  boxProps?: BoxProps;
};

export const getAvailabilityLangEnum = (
  useFutureStockAsPreOrder: boolean = true,
): Record<AvailabilityEnum | CompactAvailability, LangEnum> => ({
  PRE_ORDER: LangEnum.GLOBAL_PRE_ORDER,
  INFINITE_PRE_ORDER: LangEnum.GLOBAL_PRE_ORDER,
  MATERIAL_PRE_ORDER: useFutureStockAsPreOrder
    ? LangEnum.GLOBAL_PRE_ORDER
    : LangEnum.GLOBAL_MATERIAL_PRE_ORDER,
  READY_FOR_DELIVERY: LangEnum.GLOBAL_READY_FOR_DELIVERY,
  FUTURE_STOCK: useFutureStockAsPreOrder ? LangEnum.GLOBAL_PRE_ORDER : LangEnum.GLOBAL_FUTURE_STOCK,
  EMPTY: LangEnum.GLOBAL_EMPTY,
});

const ProductAvailabilityRenderer = ({
  useFutureStockAsPreOrder = false,
  availabilities,
  boxProps,
}: ProductAvailabilityRendererProps) => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const langEnumByAvailability = getAvailabilityLangEnum(useFutureStockAsPreOrder);
  const visibleAvailabilities = R.uniqBy(
    (av) => langEnumByAvailability[av],
    availabilities.filter((av) => av !== AvailabilityEnum.EMPTY),
  );

  return (
    <Box
      {...boxProps}
      sx={[
        {
          display: 'flex',
          flexDirection: mobileWidth ? 'column' : 'row',
          columnGap: '4px',
          rowGap: '4px',
        },
        ...(Array.isArray(boxProps?.sx) ? boxProps.sx : [boxProps?.sx]),
      ]}
    >
      {visibleAvailabilities.map((av) => {
        const label = translate(langEnumByAvailability[av]);
        const availability =
          useFutureStockAsPreOrder && av === AvailabilityEnum.FUTURE_STOCK
            ? AvailabilityEnum.INFINITE_PRE_ORDER
            : av;
        return (
          <ProductAvailabilityChip
            availability={availability}
            label={label}
            hideTypeTag
            key={langEnumByAvailability[av]}
          />
        );
      })}
    </Box>
  );
};

export default ProductAvailabilityRenderer;
